import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

/**
 * How often an open tab asks whether it is still current.
 *
 * Long, deliberately. The check that actually catches people is the one on
 * refocus below — a partner comes back to a tab left open since yesterday, and
 * hears about it then. The interval only exists for a tab that stays visible
 * across a deploy, which is a screen somebody is sitting in front of and in no
 * hurry to be interrupted.
 */
const pollIntervalMs = 10 * 60_000;

/**
 * True once the server is serving a newer build than the one running here.
 *
 * The point is not the new features; it is that this bundle's hashed assets stop
 * existing at the next deploy, so a tab left open long enough is one lazy fetch
 * or one hard refresh away from a version that is no longer there. Telling the
 * client rather than reloading under them is the whole design — see
 * `UpdateBanner`.
 *
 * Never fires in development, where `__BUILD_ID__` belongs to the dev server and
 * `/version.json` is not built at all.
 */
export function useUpdateAvailable(): boolean {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (import.meta.env.DEV || updateAvailable) return;

    let cancelled = false;

    const check = async () => {
      if (document.hidden) return;
      try {
        // `no-store` and not merely `no-cache`: this request is the thing that
        // detects staleness, so it is the one request that must never itself be
        // answered from a cache.
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const { buildId } = (await res.json()) as { buildId?: string };
        if (!cancelled && buildId && buildId !== __BUILD_ID__) setUpdateAvailable(true);
      } catch {
        // Offline, or a rewrite handing back index.html because the file is not
        // there. Both mean "ask again later" and neither is worth a console line
        // on a timer.
      }
    };

    const interval = setInterval(() => void check(), pollIntervalMs);
    const onVisible = () => void check();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [updateAvailable]);

  return updateAvailable;
}

/**
 * Once an update is pending, turns the client's next navigation into a full page
 * load instead of a client-side one.
 *
 * This is the one moment a reload costs nothing: they have just left whatever
 * they were doing, so there is no half-filled form to lose, and the new page was
 * going to be blank for a moment regardless. Most clients will therefore never
 * see the banner at all — it is left to the person who stays put on one screen.
 *
 * Deliberately still armed after the banner is dismissed. Dismissing means "stop
 * showing me that", not "keep me on the old build"; this costs them nothing and
 * says nothing.
 *
 * Keyed on pathname alone, so changing a filter or any other query string on the
 * page somebody is working in does not pull the floor out from under them.
 */
export function useReloadOnNavigate(enabled: boolean): void {
  const { pathname } = useLocation();
  const pathnameWhenArmed = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      pathnameWhenArmed.current = null;
      return;
    }
    // First run after arming establishes where the client already was; only a
    // move away from there is a navigation.
    if (pathnameWhenArmed.current === null) {
      pathnameWhenArmed.current = pathname;
      return;
    }
    if (pathnameWhenArmed.current === pathname) return;
    // The router has already put the new path in the address bar, so this
    // reloads into the page they asked for rather than the one they left.
    window.location.reload();
  }, [enabled, pathname]);
}
